import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Linkedin, 
  Twitter, 
  Globe,
  ArrowUp
} from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePhoneClick = () => {
    window.open('tel:+919074140432', '_self');
  };

  const handleEmailClick = () => {
    window.open('mailto:info@x2infotech.in?subject=Inquiry from Website&body=Hello X2InfoTech Team,%0D%0A%0D%0AI am interested in your services. Please contact me.%0D%0A%0D%0AThank you.', '_self');
  };

  const quickLinks = [
    { label: 'About Us', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'Careers', href: '#careers' },
    { label: 'Contact', href: '#contact' }
  ];

  const services = [
    'Web Development & Portals',
    'CRM & ERP Systems',
    'Mobile App Development',
    'API Development & Integration',
    'Cloud Solutions & DevOps',
    'Software Maintenance & AMC',
    'Custom Web & App Engineering',
    'SaaS-Based Cloud Platforms'
  ];

  return (
    <footer className="bg-brand-secondary text-white overflow-hidden">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1 animate-fade-in-up">
            <div className="flex items-center mb-4 transform hover:scale-105 transition-transform duration-200">
              <img 
                src="/logofull.webp" 
                alt="X2InfoTech Logo" 
                className="h-10 w-auto filter brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Transforming businesses with innovative IT solutions and cutting-edge 
              technology consulting services for over a decade.
            </p>
            <div className="flex space-x-4">
              <button className="bg-brand-primary text-white p-2 rounded-lg hover:bg-brand-accent transition-all duration-200 transform hover:scale-110 hover:-translate-y-1 group">
                <Linkedin className="h-5 w-5 group-hover:animate-pulse" />
              </button>
              <button className="bg-brand-accent text-white p-2 rounded-lg hover:bg-green-500 transition-all duration-200 transform hover:scale-110 hover:-translate-y-1 group">
                <Twitter className="h-5 w-5 group-hover:animate-pulse" />
              </button>
              <button className="bg-gray-700 text-white p-2 rounded-lg hover:bg-gray-600 transition-all duration-200 transform hover:scale-110 hover:-translate-y-1 group">
                <Globe className="h-5 w-5 group-hover:animate-pulse" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-fade-in-up animation-delay-200">
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-all duration-200 hover:translate-x-1 transform inline-block"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="animate-fade-in-up animation-delay-400">
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <span className="text-gray-400 text-sm hover:text-white transition-colors duration-200 cursor-default">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="animate-fade-in-up animation-delay-600">
            <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-start group">
                <MapPin className="h-5 w-5 text-brand-primary mr-2 mt-0.5 flex-shrink-0 group-hover:animate-bounce" />
                <div>
                  <p className="text-gray-400 text-sm">SAINIK COLONY, PINTO PARK, GOLA KA MANDIR</p>
                  <p className="text-gray-400 text-sm">GWALIOR, MADHYA PRADESH, INDIA, 474005</p>
                </div>
              </div>
              <button 
                onClick={handlePhoneClick}
                className="flex items-center group hover:text-white transition-colors duration-200"
              >
                <Phone className="h-5 w-5 text-brand-primary mr-2 flex-shrink-0 group-hover:animate-pulse" />
                <p className="text-gray-400 text-sm group-hover:text-white">+91 90741 40432</p>
              </button>
              <button 
                onClick={handleEmailClick}
                className="flex items-center group hover:text-white transition-colors duration-200"
              >
                <Mail className="h-5 w-5 text-brand-primary mr-2 flex-shrink-0 group-hover:animate-bounce" />
                <p className="text-gray-400 text-sm group-hover:text-white">info@x2infotech.in</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm animate-fade-in-up">
              © 2024 X2InfoTech. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0 animate-fade-in-up animation-delay-200">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Terms of Service
              </a>
              <button 
                onClick={scrollToTop}
                className="bg-brand-primary text-white p-2 rounded-lg hover:bg-brand-accent transition-all duration-200 transform hover:scale-110 hover:-translate-y-1 group"
                title="Back to top"
              >
                <ArrowUp className="h-4 w-4 group-hover:animate-bounce" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;